import { useMemo } from 'react';
import { assetTokenRatesAtom } from '../atoms';
import { poolTokenRatesAtom } from '../atoms/pool';
import { useAtom } from '../lib/state';
import { Decimal } from '../datastructures';
import { buildConfigTickerToPriceMap, resolveTokenPrice } from './tokenPriceResolver';

/**
 * React hook that provides a price resolver function for any token.
 * Wraps the pure utility functions from tokenPriceResolver.ts
 * so they can be used reactively in components.
 *
 * Returns: (address, symbol?) => Decimal | undefined
 */
export function useTokenPriceResolver(): (address: string, symbol?: string) => Decimal | undefined {
  const [assetTokenRates] = useAtom(assetTokenRatesAtom);
  const [poolTokenRates] = useAtom(poolTokenRatesAtom);

  const configTickerToPrice = useMemo(
    () => buildConfigTickerToPriceMap(poolTokenRates, assetTokenRates.rate),
    [poolTokenRates, assetTokenRates.rate],
  );

  return useMemo(
    () => (address: string, symbol?: string) =>
      resolveTokenPrice(address, symbol, poolTokenRates, assetTokenRates.rate, configTickerToPrice),
    [poolTokenRates, assetTokenRates.rate, configTickerToPrice],
  );
}
